<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/University.svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast } from "$app/utils"
  import PodcastEpisode from "./PodcastEpisode.svelte"
  import Sidebar from "./Sidebar.svelte"
  import episodes_svg from "$app/images/university/episodes.svg"
  import { ArrowLeft } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    podcast_episodes: PodcastEpisodeType[]
    current_user: UserType
  }

  let props = $props()
  let { plan, current_user }: Props = props

  let podcast_episodes: PodcastEpisodeType[] = $state(
    props.podcast_episodes || []
  )

  let isLoading = $state(false)
  let page = 1
  let totalPages = 1

  let vis = true
  function checkInfiniteLoad(): void {
    const totalPageHeight = document.body.scrollHeight
    const scrollPoint = window.scrollY + window.innerHeight + 500

    const visible = scrollPoint >= totalPageHeight

    if (visible && vis) {
      vis = false
      getEpisodes()
      setTimeout(() => (vis = true), 1000)
    }
  }

  onMount(() => {
    appStore.current_user = current_user
    appStore.plan = plan

    isLoading = true
    getEpisodes()

    let prev = new Date().getTime()
    let now = 0
    const delay = 10
    window.onscroll = function () {
      now = new Date().getTime()
      if (now - prev > delay) {
        checkInfiniteLoad()
        prev = now
      }
    }
  })

  // Get all statements for this section
  async function getEpisodes(reset = false): Promise<void> {
    if (reset) {
      totalPages = 0
      page = 1
      podcast_episodes = []
    }
    isLoading = true

    if (totalPages && page > totalPages) {
      isLoading = false
      return
    }

    try {
      const url = `/plans/${appStore.plan.id}/podcast/fetch.json?page=${page}`
      const resp = await xhr.get(url)
      totalPages = +resp.headers["total-pages"]
      page++

      podcast_episodes = [...podcast_episodes, ...resp.data]

      window.history.pushState("Podcast Episodes", ``)
    } catch (e) {
      isLoading = false

      toast("There was an error loading the podcast episodes", "error")
    } finally {
      setTimeout(() => {
        isLoading = false
      }, 1000)
    }
  }
</script>

<Layout {...props}>
  <div
    class="bg-gradient-to-r from-course-bg-light to-course-bg-dark w-full border-t-border-b-2 border-gray-800"
  >
    <div
      class="mx-auto max-w-7xl grid grid-cols-1 md:grid-cols-3 items-center sm:px-6 lg:px-8"
    >
      <div class="flex justify-start items-center">
        <a
          href="/plans/{appStore.plan.id}/university"
          class="flex items-center justify-start hover:border-b hover:font-bold text-white"
        >
          <ArrowLeft class="h-6 w-6 text-white mr-1" />
          Back to University
        </a>
      </div>
      <div class="py-3">
        <div class="flex items-center justify-center">
          <img
            class="inline-block h-6 w-6 rounded-full shadow bg-gray-50 p-1"
            src={appStore.plan.logo_url}
            alt="Logo"
          />
          <p
            class="text-base leading-tight text-gray-200 ml-2"
            data-name="plan-name"
          >
            {appStore.plan.name}
          </p>
        </div>
        <h1 class="text-white text-2xl text-center mt-1">
          Surfacing Leaders Podcast
        </h1>
      </div>
    </div>
  </div>

  <div class="max-w-xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
    <div class="grid grid-cols-3">
      <div class="relative">
        <Sidebar />
      </div>
      <div class="col-span-2">
        <main
          class="border-t border-slate-200 lg:border-t-0 relative bg-indigo-50"
        >
          <img
            src={episodes_svg}
            class="absolute left-0 top-0 h-20 w-full"
            alt="Episodes"
          />

          <div class="relative">
            <div class="pb-12 pt-16 sm:pb-4 lg:pt-12">
              <div class="lg:px-8">
                <div class="lg:max-w-4xl">
                  <div
                    class="mx-auto px-4 sm:px-6 md:max-w-2xl md:px-4 lg:px-0"
                  >
                    <h1 class="text-2xl font-bold leading-7 text-slate-900">
                      Episodes
                    </h1>
                  </div>
                </div>
              </div>
              <div
                class="divide-y divide-slate-100 sm:mt-4 lg:mt-8 lg:border-t lg:border-slate-100"
              >
                <div data-name="video-list" id="infinite-scroll">
                  {#each podcast_episodes as podcast_episode (podcast_episode.id)}
                    <PodcastEpisode {podcast_episode} show_link={true} />
                  {/each}
                </div>
                {#if isLoading}
                  <div class="py-6 w-full">
                    <div class="text-center flex justify-center">
                      <Pulse
                        size="140"
                        color="#cdcdcd"
                        unit="px"
                        duration="1s"
                      />
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</Layout>
